import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Cpu, Upload, X, FileArchive, CheckCircle, AlertCircle, AlertTriangle } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { useDropzone, type FileRejection } from 'react-dropzone'
import { useAuth } from '../../hooks/useAuth'
import { modelsApi } from '../../services/api/models'
import { validateSolveZip, type SolveZipValidation } from '../../utils/validateSolveZip'

const MAX_SIZE_BYTES = 5 * 1024 * 1024 * 1024 // 5 GB (R2 single-PUT ceiling)
const MAX_SIZE_LABEL = '5GB'

interface SolveSettings {
  name: string
  description: string
  category: string
  tags: string[]
  visibility: 'public' | 'private' | 'unlisted'
  featured: boolean
}

type UploadStatus = 'idle' | 'validating' | 'uploading' | 'uploaded' | 'error'

// Mirrors the SolveJob shape returned by GET /api/v2/admin/solve/:jobId
interface SolveJob {
  jobId: string
  status: 'pending' | 'uploaded' | 'queued' | 'processing' | 'completed' | 'failed'
  progress: number
  message?: string
  modelId?: string
  error?: string
}

export default function NewSolve() {
  const navigate = useNavigate()
  const { token, adminEmail } = useAuth()
  const apiUrl = import.meta.env.VITE_API_URL

  const [file, setFile] = useState<File | null>(null)
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>('idle')
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [validation, setValidation] = useState<SolveZipValidation | null>(null)
  const [jobId, setJobId] = useState<string>('')

  const [triggering, setTriggering] = useState(false)
  const [job, setJob] = useState<SolveJob | null>(null)
  const [tagInput, setTagInput] = useState('')

  const [settings, setSettings] = useState<SolveSettings>({
    name: '',
    description: '',
    category: '',
    tags: [],
    visibility: 'public',
    featured: false,
  })

  const { data: categoriesData } = useQuery({
    queryKey: ['categories'],
    queryFn: () => modelsApi.getCategories(),
  })
  const categories = categoriesData?.data || []

  const authHeaders = {
    Authorization: `Bearer ${token}`,
    'X-Admin-Email': adminEmail!,
  }

  // --- Upload the zip: start (reserve jobId + presigned URL) then PUT to R2 ---
  const uploadZip = async (zip: File) => {
    setUploadStatus('uploading')
    setUploadProgress(0)
    setUploadError(null)
    try {
      const startRes = await fetch(`${apiUrl}/api/v2/admin/solve/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          filename: zip.name,
          fileSize: zip.size,
          mimeType: zip.type || 'application/zip',
        }),
      })
      if (!startRes.ok) {
        const detail = await startRes.text().catch(() => '')
        throw new Error(`Failed to start job (${startRes.status}): ${detail || startRes.statusText}`)
      }
      const { data } = await startRes.json()
      const { jobId: newJobId, presignedUrl } = data as { jobId: string; presignedUrl?: string | null }

      const xhr = new XMLHttpRequest()
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) setUploadProgress(Math.round((e.loaded / e.total) * 100))
      })
      await new Promise<void>((resolve, reject) => {
        xhr.onload = () =>
          xhr.status >= 200 && xhr.status < 300
            ? resolve()
            : reject(new Error(`Upload failed with status ${xhr.status}: ${xhr.statusText || 'no response'}`))
        xhr.onerror = () => reject(new Error('Upload failed (network or CORS error)'))

        if (presignedUrl) {
          xhr.open('PUT', presignedUrl)
          xhr.setRequestHeader('Content-Type', zip.type || 'application/zip')
        } else {
          // Proxy fallback (capped ~100MB by Cloudflare).
          xhr.open('PUT', `${apiUrl}/api/v2/admin/solve/proxy/upload/${newJobId}`)
          xhr.setRequestHeader('Authorization', `Bearer ${token}`)
          xhr.setRequestHeader('X-Admin-Email', adminEmail!)
          xhr.setRequestHeader('Content-Type', zip.type || 'application/zip')
        }
        xhr.send(zip)
      })

      setJobId(newJobId)
      setUploadProgress(100)
      setUploadStatus('uploaded')
      // Prefill the model name from the archive filename if empty.
      if (!settings.name) {
        const name = zip.name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ')
        setSettings((prev) => ({ ...prev, name }))
      }
    } catch (err) {
      console.error('Solve upload error:', err)
      setUploadStatus('error')
      setUploadError(err instanceof Error ? err.message : 'Upload failed')
    }
  }

  // Validate the zip structure client-side, then upload. Hard errors block the
  // upload; warnings (e.g. missing sparse/0) are surfaced but don't block.
  const validateThenUpload = async (zip: File) => {
    setFile(zip)
    setUploadError(null)
    setValidation(null)
    setUploadStatus('validating')
    let result: SolveZipValidation
    try {
      result = await validateSolveZip(zip)
    } catch (err) {
      setUploadStatus('error')
      setUploadError(err instanceof Error ? err.message : 'Failed to validate the zip')
      return
    }
    setValidation(result)
    if (!result.valid) {
      setUploadStatus('error')
      setUploadError(result.errors.join('\n'))
      return
    }
    await uploadZip(zip)
  }

  const onDrop = useCallback((accepted: File[]) => {
    const zip = accepted[0]
    if (!zip) return
    void validateThenUpload(zip)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onDropRejected = useCallback((rejections: FileRejection[]) => {
    const r = rejections[0]
    if (!r) return
    setFile(r.file)
    setUploadStatus('error')
    const messages = r.errors.map((e) => {
      if (e.code === 'file-too-large') {
        return `檔案 ${(r.file.size / (1024 * 1024 * 1024)).toFixed(2)} GB;上限為 ${MAX_SIZE_LABEL}`
      }
      if (e.code === 'file-invalid-type') return '只接受 .zip 壓縮檔'
      if (e.code === 'too-many-files') return '一次只能上傳一個檔案'
      return e.message
    })
    setUploadError(messages.join('; '))
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    accept: { 'application/zip': ['.zip'], 'application/x-zip-compressed': ['.zip'] },
    maxFiles: 1,
    maxSize: MAX_SIZE_BYTES,
    disabled: uploadStatus === 'uploading' || uploadStatus === 'validating',
  })

  // --- Poll job status once solving is triggered ---
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  useEffect(() => {
    if (!jobId || !job || job.status === 'completed' || job.status === 'failed') {
      if (pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
      return
    }
    pollRef.current = setInterval(async () => {
      try {
        const res = await fetch(`${apiUrl}/api/v2/admin/solve/${jobId}`, { headers: authHeaders })
        if (res.ok) {
          const { data } = await res.json()
          setJob(data as SolveJob)
        }
      } catch {
        /* transient poll error — keep polling */
      }
    }, 3000)
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, job?.status])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (uploadStatus !== 'uploaded' || !jobId) {
      alert('請先上傳照片素材壓縮檔')
      return
    }
    setTriggering(true)
    try {
      const res = await fetch(`${apiUrl}/api/v2/admin/solve/trigger`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ jobId, settings }),
      })
      const body = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(body?.error || `Failed to trigger solving (${res.status})`)
      setJob({ jobId, status: body.data?.status || 'queued', progress: 0 })
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to trigger solving')
    } finally {
      setTriggering(false)
    }
  }

  const addTag = () => {
    const t = tagInput.trim()
    if (t && !settings.tags.includes(t)) {
      setSettings((prev) => ({ ...prev, tags: [...prev.tags, t] }))
      setTagInput('')
    }
  }
  const removeTag = (tag: string) =>
    setSettings((prev) => ({ ...prev, tags: prev.tags.filter((t) => t !== tag) }))

  const resetUpload = () => {
    setFile(null)
    setUploadStatus('idle')
    setUploadProgress(0)
    setUploadError(null)
    setValidation(null)
    setJobId('')
  }

  const isSolving = job && (job.status === 'queued' || job.status === 'processing')
  const isDone = job && (job.status === 'completed' || job.status === 'failed')

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <button
              onClick={() => navigate('/admin/models')}
              className="mr-4 text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-xl font-semibold text-gray-900">Cloud Solve</h1>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Zip upload */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Photo Material Zip File</h2>

            {uploadStatus === 'idle' ? (
              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                  isDragActive ? 'border-indigo-600 bg-indigo-50' : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <input {...getInputProps()} />
                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-700 font-medium mb-2">
                  {isDragActive ? 'Drop to upload' : 'Upload photo assets ZIP file (Drag & drop or click to browse)'}
                </p>
                <p className="text-sm text-gray-500">Drag &amp; drop your photo material zip here</p>
                <p className="text-xs text-gray-400 mt-2">
                  Only accepts .zip files (max {MAX_SIZE_LABEL}). Uploaded files will be processed in the cloud.
                  <br />
                  Expected: <code className="bg-gray-100 px-1 rounded">camXX/&lt;number&gt;.jpg</code> image
                  sequences, and optionally <code className="bg-gray-100 px-1 rounded">sparse/0/</code> COLMAP
                  camera info. Without sparse/0, camera poses are auto-estimated.
                </p>
              </div>
            ) : (
              <div className="border rounded-lg p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start min-w-0">
                    <FileArchive className="w-8 h-8 text-gray-400 mr-3 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 truncate">{file?.name}</p>
                      {file && (
                        <p className="text-sm text-gray-500">{(file.size / (1024 * 1024)).toFixed(2)} MB</p>
                      )}
                    </div>
                  </div>
                  {(uploadStatus === 'error' || (uploadStatus === 'uploaded' && !job)) && (
                    <button type="button" onClick={resetUpload} className="text-gray-400 hover:text-gray-600">
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>

                {uploadStatus === 'validating' && (
                  <div className="flex items-center text-gray-600">
                    <svg className="animate-spin w-5 h-5 mr-2 text-indigo-600" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                    </svg>
                    <span className="font-medium">Validating zip structure...</span>
                  </div>
                )}

                {uploadStatus === 'uploading' && (
                  <div>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-gray-600">Uploading...</span>
                      <span className="text-gray-900 font-medium">{uploadProgress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  </div>
                )}

                {uploadStatus === 'uploaded' && (
                  <div className="flex items-center text-green-600">
                    <CheckCircle className="w-5 h-5 mr-2" />
                    <span className="font-medium">Upload complete, ready to solve</span>
                  </div>
                )}

                {uploadStatus === 'error' && (
                  <div className="flex items-start text-red-600">
                    <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium">
                        {validation && !validation.valid ? 'Invalid photo material zip' : 'Upload failed'}
                      </p>
                      {(validation && !validation.valid ? validation.errors : uploadError ? [uploadError] : []).map(
                        (msg, i) => (
                          <p key={i} className="text-sm mt-1">{msg}</p>
                        ),
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Validation summary + warnings (shown for a passing check too) */}
            {validation && validation.valid && (uploadStatus === 'uploading' || uploadStatus === 'uploaded') && (
              <div className="mt-4 space-y-3">
                {!validation.skipped && (
                  <div className="flex items-center text-sm text-gray-600">
                    <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                    <span>
                      {validation.camCount} camera folder(s), {validation.imageCount} image(s)
                      {validation.hasSparse ? ' · sparse/0 camera info detected' : ' · no sparse/0 (poses will be estimated)'}
                    </span>
                  </div>
                )}
                {validation.warnings.map((w, i) => (
                  <div key={i} className="flex items-start p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800">
                    <AlertTriangle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                    <p className="text-sm">{w}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Model settings — applied automatically when solving finishes */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-1">Model Settings</h2>
            <p className="text-sm text-gray-500 mb-4">After solving is complete, the resulting model will be automatically uploaded to the Gallery based on the following settings.</p>

            <div className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  id="name"
                  type="text"
                  required
                  value={settings.name}
                  onChange={(e) => setSettings({ ...settings, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                  Category *
                </label>
                <select
                  id="category"
                  required
                  value={settings.category}
                  onChange={(e) => setSettings({ ...settings, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Select a category</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.slug}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  id="description"
                  rows={4}
                  value={settings.description}
                  onChange={(e) => setSettings({ ...settings, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    placeholder="Add a tag"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <button
                    type="button"
                    onClick={addTag}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                  >
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {settings.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-indigo-100 text-indigo-800"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="ml-2 text-indigo-600 hover:text-indigo-800"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Visibility */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Visibility Settings</h2>
            <div className="space-y-4">
              <div className="space-y-2">
                {(['public', 'unlisted', 'private'] as const).map((v) => (
                  <label key={v} className="flex items-center">
                    <input
                      type="radio"
                      value={v}
                      checked={settings.visibility === v}
                      onChange={(e) => setSettings({ ...settings, visibility: e.target.value as SolveSettings['visibility'] })}
                      className="mr-2"
                    />
                    <span className="text-sm">
                      <span className="font-medium capitalize">{v}</span>
                      {v === 'public' && ' - Anyone can view and download'}
                      {v === 'unlisted' && ' - Only accessible with direct link'}
                      {v === 'private' && ' - Only specific users can access'}
                    </span>
                  </label>
                ))}
              </div>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.featured}
                  onChange={(e) => setSettings({ ...settings, featured: e.target.checked })}
                  className="mr-2"
                />
                <span className="text-sm">
                  <span className="font-medium">Featured</span> - Display prominently in gallery
                </span>
              </label>
            </div>
          </div>

          {/* Solve status — shown after triggering */}
          {job && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Processing Progress</h2>
              {isSolving && (
                <div>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-gray-600">
                      {job.message || (job.status === 'queued' ? 'Queued...' : 'Processing...')}
                    </span>
                    <span className="text-gray-900 font-medium">{job.progress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${job.progress}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-2">Job ID: {job.jobId}</p>
                </div>
              )}
              {job.status === 'completed' && (
                <div className="flex items-center text-green-600">
                  <CheckCircle className="w-5 h-5 mr-2" />
                  <span className="font-medium">
                    Solve completed! Resulting model has been uploaded{job.modelId ? '' : ' (waiting for indexing)'}.
                  </span>
                </div>
              )}
              {job.status === 'failed' && (
                <div className="flex items-start text-red-600">
                  <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium">Solve failed</p>
                    <p className="text-sm mt-1">{job.error || job.message || 'Unknown error'}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={() => navigate('/admin/models')}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            {job?.status === 'completed' && job.modelId ? (
              <button
                type="button"
                onClick={() => navigate(`/admin/models/${job.modelId}/edit`)}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                View Model
              </button>
            ) : isDone ? (
              <button
                type="button"
                onClick={() => navigate('/admin/models')}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                Finish
              </button>
            ) : (
              <button
                type="submit"
                disabled={uploadStatus !== 'uploaded' || triggering || !!isSolving}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Cpu className="w-5 h-5" />
                {triggering ? 'Starting...' : isSolving ? 'Processing...' : 'Start Solving'}
              </button>
            )}
          </div>
        </form>
      </main>
    </div>
  )
}
